import { LOCAL_STORAGE_IDS } from 'Shared/constants';
import { getLocalStorage, setLocalStorage } from 'Shared/services/localStorageService';

export interface DeviceInfoModel {
  is_mobile                   : boolean;
  form_factor                 : string;
  advertised_device_os        : string;
  advertised_device_os_version: string;
  advertised_browser          : string;
  advertised_browser_version  : string;
  complete_device_name        : string;
  [x: string]                 : any
}

type DeviceInfoModelFallback = {
  os                : string;
  osVersion         : string;
  browser           : string;
  browserVersion    : string;
  completeDeviceName: string;
}

// Function to detect Operating System and its version
function detectOS(ua: string) {
  let match;

  match = ua.match(/Windows NT 10.0/);
  if (match) {
    return {
      osName    : 'Windows',
      osVersion : '10'
    };
  }

  match = ua.match(/Windows NT 6.3/);
  if (match) {
    return {
      osName    : 'Windows',
      osVersion : '8.1'
    };
  }

  match = ua.match(/Windows NT 6.2/);
  if (match) {
    return {
      osName    : 'Windows',
      osVersion : '8'
    };
  }

  match = ua.match(/Windows NT 6.1/);
  if (match) {
    return {
      osName    : 'Windows',
      osVersion : '7'
    };
  }

  match = ua.match(/Mac OS X ([0-9_]+)/);
  if (match) {
    return {
      osName    : 'macOS',
      osVersion : match[1].replace(/_/g, '.')
    };
  }

  match = ua.match(/Android ([0-9.]+)/);
  if (match) {
    return {
      osName    : 'Android',
      osVersion : match[1]
    };
  }

  match = ua.match(/iPhone OS ([0-9_]+)/);
  if (match) {
    return {
      osName    : 'iOS',
      osVersion : match[1].replace(/_/g, '.')
    };
  }

  match = ua.match(/Linux/);
  if (match) {
    return {
      osName    : 'Linux',
      osVersion : ''
    };
  }

  return {
    osName   : '',
    osVersion: ''
  };
}

// Function to detect Browser and its version
function detectBrowser(ua: string) {
  let match;

  match = ua.match(/MSIE (\d+\.\d+)/);
  if (match) {
    return {
      browserName    : 'Internet Explorer',
      browserVersion : match[1]
    };
  }

  match = ua.match(/Edg\/(\d+\.\d+)/);
  if (match) {
    return {
      browserName    : 'Edge',
      browserVersion : match[1]
    };
  }

  match = ua.match(/Chrome\/(\d+\.\d+)/);
  if (match && !/Chromium/.test(ua)) {
    return {
      browserName    : 'Chrome',
      browserVersion : match[1]
    };
  }

  match = ua.match(/Firefox\/(\d+\.\d+)/);
  if (match) {
    return {
      browserName    : 'Firefox',
      browserVersion : match[1]
    };
  }

  match = ua.match(/Safari\/(\d+\.\d+)/);
  if (match && !/Chrome/.test(ua)) {
    return {
      browserName    : 'Safari',
      browserVersion : match[1]
    };
  }

  match = ua.match(/Opera\/.*Version\/(\d+\.\d+)/);
  if (match) {
    return {
      browserName    : 'Opera',
      browserVersion : match[1]
    };
  }

  return {
    browserName    : '',
    browserVersion : ''
  };
}

// Main function to detect User Agent properties
function detectUserAgent(): DeviceInfoModelFallback {
  const ua                              = navigator.userAgent;
  const { osName, osVersion }           = detectOS(ua);
  const { browserName, browserVersion } = detectBrowser(ua);

  return {
    os                : osName,
    osVersion         : osVersion,
    browser           : browserName,
    browserVersion    : browserVersion,
    completeDeviceName: browserName
  };
}

/**
 * Fallback for detecting whether the device is a mobile device
 * @returns boolean
 */
const isMobileFallback = (): boolean => {
  /* istanbul ignore next */
  /* eslint-disable */
  return (function(a) {return (/(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino/i.test(a) || /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i.test(a.substr(0, 4)));})(navigator.userAgent || navigator.vendor || window.opera); // NOSONAR
  /* eslint-enable */
};

/**
 * Returns whether the object has any undefined properties
 * @returns boolean
 */
const hasUndefinedProperties = (obj: any): boolean => {
  return Object.keys(obj).some((key) => obj[key] === undefined);
};

/**
 * Gets wurfl data either from window library or from back up if it hasn't loaded
 */
export const getWurfl = (): DeviceInfoModel => {
  // First check the real source of truth
  if (typeof window !== 'undefined' && window.WURFL) {
    const wurflData: DeviceInfoModel = {
      is_mobile                   : window.WURFL.is_mobile,
      form_factor                 : window.WURFL.form_factor,
      advertised_device_os        : window.WURFL.advertised_device_os,
      advertised_device_os_version: window.WURFL.advertised_device_os_version,
      advertised_browser          : window.WURFL.advertised_browser,
      advertised_browser_version  : window.WURFL.advertised_browser_version,
      complete_device_name        : window.WURFL.complete_device_name,
    };

    if (!hasUndefinedProperties(wurflData)) {
      setLocalStorage(LOCAL_STORAGE_IDS.USER_DEVICE, window.WURFL, true);
      return wurflData;
    }
  }

  // Second check for the local storage cache
  const wurflCacheData = getLocalStorage(LOCAL_STORAGE_IDS.USER_DEVICE, true) || {};
  const wurflData: DeviceInfoModel = {
    is_mobile                   : wurflCacheData.is_mobile,
    form_factor                 : wurflCacheData.form_factor,
    advertised_device_os        : wurflCacheData.advertised_device_os,
    advertised_device_os_version: wurflCacheData.advertised_device_os_version,
    advertised_browser          : wurflCacheData.advertised_browser,
    advertised_browser_version  : wurflCacheData.advertised_browser_version,
    complete_device_name        : wurflCacheData.complete_device_name,
  };

  if (!hasUndefinedProperties(wurflData)) {
    return wurflData;
  }

  // If all else fails then use the fallback
  const isMobile: boolean                   = isMobileFallback();
  const deviceInfo: DeviceInfoModelFallback = detectUserAgent();

  // Limited WURFL data if WURFL hasn't loaded yet
  return {
    is_mobile                   : isMobile,
    form_factor                 : isMobile ? 'Smartphone' : 'Desktop',
    advertised_device_os        : deviceInfo.os,
    advertised_device_os_version: deviceInfo.osVersion,
    advertised_browser          : deviceInfo.browser,
    advertised_browser_version  : deviceInfo.browserVersion,
    complete_device_name        : deviceInfo.completeDeviceName,
  };
};

// Do this to set WURFL right away
if (typeof window !== 'undefined' && window.WURFL) {
  getWurfl();
}